import {
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  AfterContentInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabPanelComponent } from './tab-panel.component';

/**
 * Componente genérico de abas (tabs).
 *
 * Uso:
 * ```html
 * <app-tabs [activeTab]="abaAtiva" (activeTabChange)="abaAtiva = $event">
 *   <app-tab-panel tabId="dados" label="Dados">
 *     <!-- conteúdo -->
 *   </app-tab-panel>
 *   <app-tab-panel tabId="anexos" label="Anexos">
 *     <!-- conteúdo -->
 *   </app-tab-panel>
 * </app-tabs>
 * ```
 */
@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex h-full min-h-0 flex-col">
      <nav class="flex flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-[0.95rem]" role="tablist">
        <button
          *ngFor="let panel of panels"
          type="button"
          role="tab"
          [attr.aria-selected]="activeTab === panel.tabId"
          class="relative mr-5 whitespace-nowrap border-0 bg-transparent cursor-pointer py-[0.65rem] px-[0.1rem] font-['Open_Sans',sans-serif] text-[13px] font-normal leading-[1.5] tracking-[0] transition-colors duration-150
            text-gray-400 hover:text-gray-700
            dark:text-gray-500 dark:hover:text-gray-300"
          [class.!text-[#3b5dcc]]="activeTab === panel.tabId"
          [class.dark:!text-[#7b9ef5]]="activeTab === panel.tabId"
          [class.tab-active]="activeTab === panel.tabId"
          (click)="selectTab(panel.tabId)">
          {{ panel.label }}
          <span
            *ngIf="activeTab === panel.tabId"
            class="absolute bottom-[-1px] left-0 right-0 h-[2px] rounded-t-[1px] bg-[#3b5dcc] dark:bg-[#7b9ef5]">
          </span>
        </button>
      </nav>

      <div class="flex min-h-0 flex-1 flex-col">
        <ng-content></ng-content>
      </div>
    </div>
  `
})
export class TabsComponent implements AfterContentInit, OnChanges {
  @HostBinding('class') hostClass = 'flex flex-1 min-h-0 flex-col overflow-hidden';

  @Input() activeTab = '';
  @Output() activeTabChange = new EventEmitter<string>();

  @ContentChildren(TabPanelComponent) panels!: QueryList<TabPanelComponent>;

  ngAfterContentInit(): void {
    // Mutar panel.active aqui de forma síncrona dispara NG0100
    // (ExpressionChangedAfterItHasBeenCheckedError) em dev mode — adia pro próximo microtask.
    Promise.resolve().then(() => {
      this.updatePanelVisibility();
      if (!this.activeTab && this.panels.first) {
        this.activeTab = this.panels.first.tabId;
        this.updatePanelVisibility();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab'] && this.panels) {
      this.updatePanelVisibility();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.updatePanelVisibility();
    this.activeTabChange.emit(tabId);
  }

  private updatePanelVisibility(): void {
    this.panels?.forEach((panel) => {
      panel.active = panel.tabId === this.activeTab;
    });
  }
}
